/* eslint-disable @typescript-eslint/no-explicit-any */
import Head from 'next/head';
import { Skeleton } from '~/components/shared/Skeleton';
import 'react-tooltip/dist/react-tooltip.css';

import MonarchSection from '~/cohesion/Monarch/MonarchSection';
import { MonarchSlot, useMonarch } from '@redventures/cohesion-utils-react';
import { lazy, useEffect } from 'react';
import useBeacon from '../lib/hooks/useBeacon';
import { useVariantIds } from '~/lib/hooks/useVariantIds';

// Static import above-the-fold content
import Layout from '~/components/Layout';
import SimplifiedSolidHero from '~/components/editable/SimplifiedSolidHero';
import PromotionCTABanner from '~/components/Layout/PromotionCTABanner';

// Lazy import below-the-fold content
const BenefitsTile = lazy(
  () => import('~/components/editable/BenefitsTileCarousel')
);
const MemberFilterV2 = lazy(
  () => import('~/components/editable/MemberRewardsFilterV2')
);
const LifestyleTestimonials = lazy(
  () => import('~/components/editable/LifestyleTestimonials')
);
const MembershipType = lazy(
  () => import('~/components/editable/MembershipType')
);
const CompareTable = lazy(() => import('~/components/editable/CompareTableV2'));
const FAQ = lazy(() => import('~/components/Layout/FAQ'));

// type any: using type unknown or other value breaks
export type MonarchEvaluationsValue = { [key: string]: any };

export default function Home() {
  // Call the custom hook
  useBeacon();

  const { isDarkMode } = useVariantIds();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isDarkMode) {
      document.body.classList.add('bg-dark-mode-background');
    }
  }, [isDarkMode]);

  // If the slot is undefined, render the fallback/base site: if slot is defined by named as empty, do not render
  const { slots } = useMonarch();
  const shouldRenderSlot = (slot: any, hasFallback?: boolean) => {
    if (!slot && hasFallback) {
      return true;
    } else {
      return slot && slot.value.componentName !== 'Empty';
    }
  };

  return (
    <>
      <Head>
        <title>Walmart+</title>
      </Head>
      <div>
        <Layout
          signInButton={true}
          url={
            'https://www.walmart.com/?wplusSignupOffer=true&programId=discmemb23'
          }
          text='Claim offer now'
        >
          {/* Modal */}
          <MonarchSlot slotId='modalSlot'></MonarchSlot>

          {/* Sub Navbar */}
          {shouldRenderSlot(slots.subNavbar) && (
            <MonarchSection
              elementPosition='SUB_NAVBAR'
              componentName={slots.subNavbar.value.componentName}
            >
              <MonarchSlot slotId='subNavbar'>{null}</MonarchSlot>
            </MonarchSection>
          )}

          {/* Hero */}
          <MonarchSection
            elementPosition='HERO'
            componentName={
              slots.hero
                ? slots.hero.value.componentName
                : 'Simplified Solid Hero'
            }
          >
            <MonarchSlot fallback={<Skeleton />} slotId='hero'>
              <SimplifiedSolidHero />
            </MonarchSlot>
          </MonarchSection>
          <span id='trigger-hov-cta' />

          {/* Section 01 */}
          {shouldRenderSlot(slots.section01) && (
            <MonarchSection
              elementPosition='SECTION_01'
              componentName={slots.section01.value.componentName}
            >
              <MonarchSlot
                fallback={<Skeleton />}
                slotId='section01'
              ></MonarchSlot>
            </MonarchSection>
          )}

          {/* Section 02 */}
          {shouldRenderSlot(slots.section02) && (
            <MonarchSection
              elementPosition='SECTION_02'
              componentName={slots.section02.value.componentName}
            >
              <MonarchSlot
                fallback={<Skeleton />}
                slotId='section02'
              ></MonarchSlot>
            </MonarchSection>
          )}

          {/* Section 03 */}
          {shouldRenderSlot(slots.section03) && (
            <MonarchSection
              elementPosition='SECTION_03'
              componentName={slots.section03.value.componentName}
            >
              <MonarchSlot
                fallback={<Skeleton />}
                slotId='section03'
              ></MonarchSlot>
            </MonarchSection>
          )}

          {/* Section 04 */}
          {shouldRenderSlot(slots.section04, true) && (
            <MonarchSection
              elementPosition='SECTION_04'
              componentName={
                slots.section04
                  ? slots.section04.value.componentName
                  : 'Promotion CTA Banner'
              }
            >
              <MonarchSlot slotId='section04' fallback={<Skeleton />}>
                <PromotionCTABanner />
              </MonarchSlot>
            </MonarchSection>
          )}
          {/* Section 05 */}
          {shouldRenderSlot(slots.section05, true) && (
            <MonarchSection
              elementPosition='SECTION_05'
              componentName={
                slots.section05
                  ? slots.section05.value.componentName
                  : 'Membership Type Module'
              }
            >
              <MonarchSlot fallback={<Skeleton />} slotId='section05'>
                <MembershipType />
              </MonarchSlot>
            </MonarchSection>
          )}
          {/* Section 06 */}
          {shouldRenderSlot(slots.section06, true) && (
            <MonarchSection
              elementPosition='SECTION_06'
              componentName={
                slots.section06
                  ? slots.section06.value.componentName
                  : 'Benefits Tile Carousel'
              }
            >
              <MonarchSlot slotId='section06' fallback={<Skeleton />}>
                <BenefitsTile />
              </MonarchSlot>
            </MonarchSection>
          )}
          {/* Section 07 */}
          {shouldRenderSlot(slots.section07, true) && (
            <MonarchSection
              elementPosition='SECTION_07'
              componentName={
                slots.section07
                  ? slots.section07.value.componentName
                  : 'Member Filter V2'
              }
            >
              <MonarchSlot fallback={<Skeleton />} slotId='section07'>
                <MemberFilterV2 />
              </MonarchSlot>
            </MonarchSection>
          )}
          {/* Section 08 */}
          {shouldRenderSlot(slots.section08, true) && (
            <MonarchSection
              elementPosition='SECTION_08'
              componentName={
                slots.section08
                  ? slots.section08.value.componentName
                  : 'Lifestyle Testimonials'
              }
            >
              <MonarchSlot fallback={<Skeleton />} slotId='section08'>
                <LifestyleTestimonials />
              </MonarchSlot>
            </MonarchSection>
          )}
          {/* Section 09 */}
          {shouldRenderSlot(slots.section09) && (
            <MonarchSection
              elementPosition='SECTION_09'
              componentName={slots.section09.value.componentName}
            >
              <MonarchSlot
                fallback={<Skeleton />}
                slotId='section09'
              ></MonarchSlot>
            </MonarchSection>
          )}

          {/* Section 10 */}
          {shouldRenderSlot(slots.section10, true) && (
            <MonarchSection
              elementPosition='SECTION_10'
              componentName={
                slots.section10
                  ? slots.section10.value.componentName
                  : 'Compare Table'
              }
            >
              <MonarchSlot fallback={<Skeleton />} slotId='section10'>
                <CompareTable />
              </MonarchSlot>
            </MonarchSection>
          )}
          {/* Section 11 */}
          {shouldRenderSlot(slots.section11) && (
            <MonarchSection
              elementPosition='SECTION_11'
              componentName={slots.section11.value.componentName}
            >
              <MonarchSlot
                fallback={<Skeleton />}
                slotId='section11'
              ></MonarchSlot>
            </MonarchSection>
          )}

          {/* Section 12 */}
          {shouldRenderSlot(slots.section12, true) && (
            <MonarchSection
              elementPosition='SECTION_12'
              componentName={
                slots.section12 ? slots.section12.value.componentName : 'FAQ'
              }
            >
              <MonarchSlot fallback={<Skeleton />} slotId='section12'>
                <FAQ />
              </MonarchSlot>
            </MonarchSection>
          )}
        </Layout>
      </div>
    </>
  );
}
